import { Link } from '@mui/material';
import { FC } from 'react';
import { useEventBus } from '../../../../../hooks/use-event-bus';
import { WarningMessage } from '../../../appt-request-flow/status-dialog/styles';
import { CreatePayerSetupWizardLink } from '../../utils/iv-deep-link';

const CREDENTIALS_PAGE_ID = 102;

export interface VerifiedInsuranceWarningProps {
  locationId: number;
  carrierNameId?: number;
  rwErrorCode?: string;
  websiteErrorCode?: string;
  connectionStatus?: string;
  ediErrorMessage?: string;
}

export const VerifiedInsuranceWarning: FC<VerifiedInsuranceWarningProps> = (props) => {
  const { locationId, carrierNameId, rwErrorCode, websiteErrorCode, connectionStatus, ediErrorMessage } = props;
  const { publish } = useEventBus();

  const patientDataErrorCodes = [
    'patient_member_name_error',
    'patient_member_dob_error',
    'patient_member_group_number_error',
    'patient_member_id_duplicate_error',
    'patient_not_found_error'
  ];

  const goToSetupCredentialsPage = () => {
    if (!carrierNameId) {
      console.error('Carrier Name ID is missing');

      return;
    }

    publish({
      messageType: 'navigate',
      data: {
        ...CreatePayerSetupWizardLink(locationId, carrierNameId, CREDENTIALS_PAGE_ID),
        crossFrame: true
      }
    });
  };

  const getWarningMessage = (rwErrorCode?: string, websiteErrorCode?: string, connectionStatus?: string) => {
    // A classified KayN/Stedi EDI error is the source of truth when present.
    if (ediErrorMessage) {
      return <WarningMessage>{ediErrorMessage}</WarningMessage>;
    }

    if (rwErrorCode === 'missing_credentials') {
      return (
        <WarningMessage>
          Your payer portal credentials are missing, causing only EDI data retrieval. To improve data accuracy, please
          update your credentials. <Link onClick={goToSetupCredentialsPage}>Click here to fix now.</Link>
        </WarningMessage>
      );
    }

    if (websiteErrorCode === 'provider_not_authorized' && connectionStatus === 'auth_failed') {
      return (
        <WarningMessage>
          Your payer portal credentials are invalid, causing only EDI data retrieval. To improve data accuracy, please
          update your credentials. <Link onClick={goToSetupCredentialsPage}>Click here to fix now.</Link>
        </WarningMessage>
      );
    }

    if (websiteErrorCode === 'provider_not_authorized' && connectionStatus === 'mfa_timeout') {
      return (
        <WarningMessage>
          An error with multi-factor authentication (MFA) is limiting retrieval to only EDI data. To improve data
          accuracy, please update your credentials.{' '}
          <Link onClick={goToSetupCredentialsPage}>Click here to fix now.</Link>
        </WarningMessage>
      );
      // #### This will be added back when they turn on website retry
      // }else if(websiteErrorCode === 'payer_unable_to_respond') {
      //  return (
      //    <WarningMessage>
      //      The payer’s portal is unresponsive resulting in only EDI data retrieval.
      //      We will continue to retry the payer portal for up to 5 hours to improve the results.
      //    </WarningMessage>
      //  )
    }

    if (websiteErrorCode === 'kayn_edi_only') {
      return (
        <WarningMessage>
          Verification results are based on EDI data retrieval.
        </WarningMessage>
      );
    }

    if (websiteErrorCode && patientDataErrorCodes.includes(websiteErrorCode)) {
      return (
        <WarningMessage>
          An issue with invalid patient data prevented us from retrieving information from the payer portal, resulting
          in only EDI data retrieval. Please verify the patient data in your PMS.
        </WarningMessage>
      );
    }

    if (websiteErrorCode && !patientDataErrorCodes.includes(websiteErrorCode)) {
      return (
        <WarningMessage>
          An issue prevented us from retrieving data from the payer portal, resulting in only EDI data retrieval.
        </WarningMessage>
      );
    }

    return <></>;
  };

  return <>{getWarningMessage(rwErrorCode, websiteErrorCode, connectionStatus)}</>;
};
